<template>
	<!-- eslint-disable max-lines-->
	<ConnectorServiceLayout>
		<template #content>
			<SystemList
				:systems="systemListSystems"
				:user-has-log-view-permission="userHasLogViewPermission"
				:user-has-full-connector-service-permission="userHasFullConnectorServicePermission"
				@toggle-system-enabled="onSystemIntegrationActiveChange"
				@toggle-service-enabled="onToggleServiceEnabled"
				@delete-service="onDeleteService"
				@view-failures="onViewFailures"
				@edit-system="onEditSystem"
				@export-system="onExportSystem"
				@export-service="onExportService"
				@clone-system="onCloneSystem"
				@delete-system="onSystemIntegrationRemove"
				@edit-service="onEditService"
				@clone-service="onCloneService"
				@create-service="onCreateService"
				@create-system="onCreateSystem"
			/>
			<ConfirmDeleteModal
				v-if="!!systemIntegrationToBeRemoved && userHasFullConnectorServicePermission"
				:entity-name="systemIntegrationToBeRemoved && systemIntegrationToBeRemoved.integrationSystemName"
				entity-type="System"
				@close="systemIntegrationToBeRemoved = null"
				@delete="onSystemIntegrationConfirmRemove"
			>
				<template #additional-content>
					<div
						v-if="systemIntegrationToBeRemoved && getServicesForSystem(systemIntegrationToBeRemoved).length > 0"
						class="mt-2"
					>
						<div class="text-body-2 mb-1">
							The following integrations will also be deleted:
						</div>
						<ul class="pl-4">
							<li
								v-for="svc in getServicesForSystem(systemIntegrationToBeRemoved)"
								:key="svc.id"
							>
								{{ svc.name }}
							</li>
						</ul>
					</div>
				</template>
			</ConfirmDeleteModal>
			<ConfirmDeleteModal
				v-if="!!integrationServiceToBeRemoved && userHasFullConnectorServicePermission"
				:entity-name="integrationServiceToBeRemoved && integrationServiceToBeRemoved.name"
				entity-type="Integration"
				@close="integrationServiceToBeRemoved = null"
				@delete="onIntegrationServiceConfirmRemove"
			/>
			<ConnectorServiceConfirmationModal
				v-if="systemIntegrationToBeChanged && userHasFullConnectorServicePermission"
				:entity-name="systemIntegrationToBeChanged && systemIntegrationToBeChanged.integrationSystemName"
				:active="systemIntegrationToBeChanged && systemIntegrationToBeChanged.enabled"
				type="System"
				@cancel="systemIntegrationToBeChanged = null"
				@confirm="onSystemIntegrationEnableDisable"
			/>
			<SystemCreationOptionsModal
				v-if="showSystemCreationOptionsModal && userHasFullConnectorServicePermission"
				@close="showSystemCreationOptionsModal = false"
				@select-template="onSelectTemplate"
				@select-custom="onSelectCustom"
				@import-success="onImportSuccess"
			/>
			<TemplateSelectionModal
				v-if="showTemplateSelectionModal && userHasFullConnectorServicePermission"
				@close="showTemplateSelectionModal = false"
				@back="onBackToOptions"
				@template-selected="onTemplateSelected"
				@select-custom="onSelectCustom"
			/>
			<ServiceCreationOptionsModal
				v-if="showServiceCreationOptionsModal && userHasFullConnectorServicePermission"
				:system-name="selectedSystemForService && selectedSystemForService.integrationSystemName"
				:has-available-templates="hasAvailableServiceTemplates"
				:system-integration-id="selectedSystemForService && selectedSystemForService.id"
				@close="showServiceCreationOptionsModal = false"
				@select-template="onSelectServiceTemplate"
				@select-custom="onSelectServiceCustom"
				@import-success="onServiceImportSuccess"
			/>
			<ServiceTemplateSelectionModal
				v-if="showServiceTemplateSelectionModal && userHasFullConnectorServicePermission"
				:system-name="selectedSystemForService && selectedSystemForService.integrationSystemName"
				:system-template="getSystemTemplate(selectedSystemForService)"
				@close="showServiceTemplateSelectionModal = false"
				@back="onBackToServiceOptions"
				@template-selected="onServiceTemplateSelected"
				@select-custom="onSelectServiceCustom"
			/>
			<ManageSystemIntegrationModal
				v-if="showManageSystemModal && userHasFullConnectorServicePermission"
				:system-integration-to-edit="systemToEdit"
				:lookup-data="systemIntegrationLookupData"
				:clone-mode="cloneMode"
				:template="selectedTemplate"
				:show-back-button="showBackButton"
				@close="closeManageSystemModal"
				@back="onBackFromManageSystem"
				@system-created="onSystemCreated"
				@system-updated="onSystemUpdated"
			/>
			<IntegrationServiceFailuresSheet
				v-if="showFailuresModal"
				:is-open="showFailuresModal"
				:service-name="selectedServiceForFailures && selectedServiceForFailures.name"
				:integration-id="selectedServiceForFailures && selectedServiceForFailures.id"
				@close="showFailuresModal = false"
			/>
			<SystemExportSheet
				v-if="showSystemExportSheet"
				:is-open="showSystemExportSheet"
				:system-integration="selectedSystemForExport"
				:integration-services="getServicesForSystem(selectedSystemForExport)"
				@close="showSystemExportSheet = false"
			/>
		</template>
	</ConnectorServiceLayout>
</template>

<script>
/* eslint-disable max-lines */
import { mapGetters } from 'vuex'
import { SystemList } from '../../../components/component-library-wrapper.js'
import { MANAGE_INTEGRATION_SERVICE } from '../../../router/route-names.js'
import { showSnackbar } from '../../../../../shared/state/snackbar.js'
import ConfirmDeleteModal from '../../../../../shared/components/confirm-delete-modal.vue'
import ConnectorServiceConfirmationModal from './connector-service-confirmation-modal.vue'
import ConnectorServiceLayout from './connector-service-layout.vue'
import SystemCreationOptionsModal from './system-creation-options-modal.vue'
import TemplateSelectionModal from './template-selection-modal.vue'
import ServiceCreationOptionsModal from './service-creation-options-modal.vue'
import ServiceTemplateSelectionModal from './service-template-selection-modal.vue'
import ManageSystemIntegrationModal from './manage-system-integration-modal.vue'
import IntegrationServiceFailuresSheet from './integration-service-failures-sheet.vue'
import SystemExportSheet from './system-export-sheet.vue'
import {
	getSystemIntegrations,
	getIntegrationServices,
	deleteSystemIntegration,
	deleteIntegrationService,
	enableDisableSystemIntegration,
	enableDisableIntegrationService
} from '../../../../../shared/utils/api/connector-service.js'
import { getIntegrationFailures } from '../../../../../shared/utils/api/log-viewer.js'
import { SEE_AUDIT_AND_LOG_MODULE, CONNECTOR_SERVICE_MODULE_FULL_PERMISSIONS } from '../../../../../shared/permissions/admin-portal-permissions.js'
import { SYSTEM_TEMPLATES } from '../../../../../shared/lib/system-integration-templates.js'
export default {
	components: {
		SystemList,
		ConnectorServiceLayout,
		ConnectorServiceConfirmationModal,
		ConfirmDeleteModal,
		SystemCreationOptionsModal,
		TemplateSelectionModal,
		ServiceCreationOptionsModal,
		ServiceTemplateSelectionModal,
		ManageSystemIntegrationModal,
		IntegrationServiceFailuresSheet,
		SystemExportSheet
	},

	data () {
		return {
			systemIntegrations: [],
			integrationServices: [],
			systemIntegrationToBeRemoved: null,
			systemIntegrationToBeChanged: null,
			integrationServiceToBeRemoved: null,
			systemIntegrationLookupData: null,
			integrationServiceLookupData: null,
			showSystemCreationOptionsModal: false,
			showTemplateSelectionModal: false,
			showServiceCreationOptionsModal: false,
			showServiceTemplateSelectionModal: false,
			selectedSystemForService: null,
			showManageSystemModal: false,
			systemToEdit: null,
			cloneMode: false,
			selectedTemplate: null,
			showBackButton: false,
			integrationServiceFailures: [],
			showFailuresModal: false,
			selectedServiceForFailures: null,
			showSystemExportSheet: false,
			selectedSystemForExport: null
		}
	},
	computed: {
		...mapGetters('auth', ['productAreaPermission']),
		userHasLogViewPermission () {
			return this.productAreaPermission(SEE_AUDIT_AND_LOG_MODULE)
		},
		userHasFullConnectorServicePermission () {
			return this.productAreaPermission(CONNECTOR_SERVICE_MODULE_FULL_PERMISSIONS)
		},
		systemListSystems () {
			if (!this.systemIntegrations || !this.integrationServices) return []

			return this.systemIntegrations.map(system => {
				const template = this.getSystemTemplate(system)
				return {
					systemIntegration: {
						...system,
						serviceName: system.integrationSystemName,
						icon: template ? (template.icon || null) : null,
						color: template ? (template.color || null) : null,
						iconColor: template ? (template.iconColor || 'white') : 'white'
					},
					integrationServices: this.integrationServices
						.filter(service => service.integrationId === system.id)
						.map(service => ({
							...service,
							name: service.name,
							failures: this.integrationServiceFailures.filter(failure => failure.integration === service.name)
						}))
				}
			})
		},
		systemIntegrationItems () {
			return this.systemIntegrations.map(({ id, integrationSystemName }) => ({
				text: integrationSystemName,
				value: id
			}))
		},
		hasAvailableServiceTemplates () {
			if (!this.selectedSystemForService) return false

			// Find the system template that matches the selected system
			const systemTemplate = this.getSystemTemplate(this.selectedSystemForService)

			if (systemTemplate && systemTemplate.endpoints && systemTemplate.endpoints.length > 0) {
				// Known system template with its own endpoints
				return true
			}

			// For non-templated/custom systems, check if ANY system templates have endpoints
			return false
		}
	},
	created () {
		this.loadSystemIntegrations()
		this.loadIntegrationServices()
		this.loadIntegrationServiceFailures()
	},
	methods: {
		async onToggleServiceEnabled (service) {
			if (service && service.id) {
				try {
					await enableDisableIntegrationService({ id: service.id, isEnabled: service.isEnabled })

					// If service is being enabled, ensure the parent system is also enabled
					if (service.isEnabled) {
						const systemIntegration = this.systemIntegrations.find(sys => sys.id === service.integrationId)
						if (systemIntegration && !systemIntegration.enabled) {
							await enableDisableSystemIntegration({ id: systemIntegration.id, isEnabled: true })
							showSnackbar(`System "${systemIntegration.integrationSystemName}" has been automatically enabled`)
							this.loadSystemIntegrations()
						}
					} else {
						// If service is being disabled, check if it's the last enabled service for the system
						const otherEnabledServices = this.integrationServices.filter(s =>
							s.integrationId === service.integrationId &&
							s.id !== service.id &&
							s.isEnabled
						)

						// If no other enabled services exist for this system, disable the system
						if (otherEnabledServices.length === 0) {
							const systemIntegration = this.systemIntegrations.find(sys => sys.id === service.integrationId)
							if (systemIntegration && systemIntegration.enabled) {
								await enableDisableSystemIntegration({ id: systemIntegration.id, isEnabled: false })
								showSnackbar(`System "${systemIntegration.integrationSystemName}" has been automatically disabled`)
								this.loadSystemIntegrations()
							}
						}
					}

					showSnackbar(`Integration has been ${service.isEnabled ? 'enabled' : 'disabled'}`)
					this.loadIntegrationServices()
				} catch (error) {
					console.error('Error toggling service enabled state:', error)
					showSnackbar(`Failed to ${service.isEnabled ? 'enable' : 'disable'} Integration. Please try again.`, 'error')
					// Revert the service state to maintain UI consistency
					service.isEnabled = !service.isEnabled
				}
			}
		},
		onViewFailures (service) {
			this.selectedServiceForFailures = service
			this.showFailuresModal = true
		},
		onDeleteService (service) {
			this.onIntegrationServiceRemove(service)
		},
		async loadSystemIntegrations () {
			const { data: { systemIntegrations, lookupData } } = await getSystemIntegrations()
			this.systemIntegrations = systemIntegrations
			this.systemIntegrationLookupData = lookupData
		},
		async loadIntegrationServices () {
			const { data: { integrationServices, lookupData } } = await getIntegrationServices()
			this.integrationServices = integrationServices
			this.integrationServiceLookupData = lookupData
		},
		onSystemIntegrationRemove (systemIntegration) {
			this.systemIntegrationToBeRemoved = systemIntegration
		},
		onIntegrationServiceRemove (integrationService) {
			this.integrationServiceToBeRemoved = integrationService
		},
		onSystemIntegrationActiveChange (systemIntegration) {
			this.systemIntegrationToBeChanged = systemIntegration
		},
		async onIntegrationServiceToBeChanged (integrationService) {
			const systemIntegrationEnableDisable = {
				id: integrationService.id,
				isEnabled: !integrationService.isEnabled
			}
			await enableDisableIntegrationService(systemIntegrationEnableDisable)
			showSnackbar(`Integration has been ${systemIntegrationEnableDisable.isEnabled ? 'enabled' : 'disabled'}`)
			this.loadIntegrationServices()
		},
		async onSystemIntegrationEnableDisable () {
			const systemIntegrationEnableDisable = {
				id: this.systemIntegrationToBeChanged.id,
				isEnabled: !this.systemIntegrationToBeChanged.enabled
			}
			try {
				await enableDisableSystemIntegration(systemIntegrationEnableDisable)
				showSnackbar(`System has been ${systemIntegrationEnableDisable.isEnabled ? 'enabled' : 'disabled'}`)
				await this.loadSystemIntegrations()
				await this.loadIntegrationServices()
			} catch (error) {
				console.error('Failed to update system integration', error)
				showSnackbar('Failed to update system. Please try again.', 'error')
			} finally {
				this.systemIntegrationToBeChanged = null
			}
		},
		async onSystemIntegrationConfirmRemove () {
			try {
				const system = this.systemIntegrationToBeRemoved
				if (!system) return

				// Fetch services for this system and delete them first
				const services = this.getServicesForSystem(system) || []
				const serviceCount = services.length
				if (serviceCount > 0) {
					let serviceDeletionFailed = false
					for (const svc of services) {
						try {
							await deleteIntegrationService(svc.id)
						} catch (e) {
							serviceDeletionFailed = true
							console.error('Failed to delete integration', svc, e)
							showSnackbar(`Failed to delete integration "${svc.name}"`, 'error')
						}
					}
					// If any service failed to delete, stop and do not delete the system
					if (serviceDeletionFailed) {
						showSnackbar('One or more integrations could not be deleted. System was not removed.', 'error')
						return
					}
				}

				// Now delete the system
				await deleteSystemIntegration(system.id)
				if (serviceCount > 0) {
					const plural = serviceCount === 1 ? '' : 's'
					showSnackbar(`System and ${serviceCount} integration${plural} have been removed.`)
				} else {
					showSnackbar('System has been removed.')
				}
			} catch (error) {
				console.error('Failed to delete system', error)
				showSnackbar('Failed to delete system. Please try again.', 'error')
			} finally {
				this.systemIntegrationToBeRemoved = null
				await this.loadIntegrationServices()
				await this.loadSystemIntegrations()
			}
		},
		async onIntegrationServiceConfirmRemove () {
			await deleteIntegrationService(this.integrationServiceToBeRemoved.id)
			this.integrationServiceToBeRemoved = null
			this.loadIntegrationServices()
			showSnackbar('Integration has been removed.')
		},
		onEditService ({ integrationService, systemIntegration }) {
			this.$router.push({
				name: MANAGE_INTEGRATION_SERVICE,
				params: {
					integrationServiceToEdit: { ...integrationService, systemIntegrationId: systemIntegration.id },
					lookupData: this.integrationServiceLookupData,
					systemIntegrationItems: this.systemIntegrationItems,
					systemAuthenticationTypeId: systemIntegration.authenticationTypeId
				}
			})
		},
		onCreateService (systemIntegration) {
			this.selectedSystemForService = systemIntegration
			// if (!systemIntegration.templateGUID) {
			// 	this.onSelectServiceCustom()
			// } else {
			// }
			this.showServiceCreationOptionsModal = true
		},
		onCreateSystem () {
			this.showSystemCreationOptionsModal = true
		},
		onSelectTemplate () {
			this.showSystemCreationOptionsModal = false
			this.showTemplateSelectionModal = true
		},
		onSelectCustom () {
			this.showSystemCreationOptionsModal = false
			this.showTemplateSelectionModal = false
			this.systemToEdit = null
			this.cloneMode = false
			this.selectedTemplate = null
			this.showBackButton = false
			this.showManageSystemModal = true
		},
		onBackToOptions () {
			this.showTemplateSelectionModal = false
			this.showSystemCreationOptionsModal = true
		},
		onTemplateSelected (template) {
			this.showTemplateSelectionModal = false
			this.systemToEdit = null
			this.cloneMode = false
			this.selectedTemplate = template
			this.showBackButton = true
			this.showManageSystemModal = true
		},
		onCloneService ({ integrationService, systemIntegration }) {
			this.$router.push({
				name: MANAGE_INTEGRATION_SERVICE,
				params: {
					integrationServiceToEdit: { ...integrationService, systemIntegrationId: systemIntegration.id },
					lookupData: this.integrationServiceLookupData,
					systemIntegrationItems: this.systemIntegrationItems,
					cloneMode: true,
					systemAuthenticationTypeId: systemIntegration.authenticationTypeId
				}
			})
		},
		onSelectServiceTemplate () {
			this.showServiceCreationOptionsModal = false
			this.showServiceTemplateSelectionModal = true
		},
		onSelectServiceCustom () {
			this.showServiceCreationOptionsModal = false
			this.showServiceTemplateSelectionModal = false
			this.$router.push({
				name: MANAGE_INTEGRATION_SERVICE,
				params: {
					systemIntegrationId: this.selectedSystemForService.id,
					lookupData: this.integrationServiceLookupData,
					systemIntegrationItems: this.systemIntegrationItems,
					systemAuthenticationTypeId: this.selectedSystemForService.authenticationTypeId
				}
			})
		},
		onServiceImportSuccess () {
			this.showServiceCreationOptionsModal = false
			this.loadIntegrationServices()
			showSnackbar('Integration created successfully from import')
		},
		onBackToServiceOptions () {
			this.showServiceTemplateSelectionModal = false
			this.showServiceCreationOptionsModal = true
		},
		onServiceTemplateSelected (template) {
			this.showServiceTemplateSelectionModal = false
			this.$router.push({
				name: MANAGE_INTEGRATION_SERVICE,
				params: {
					systemIntegrationId: this.selectedSystemForService.id,
					lookupData: this.integrationServiceLookupData,
					systemIntegrationItems: this.systemIntegrationItems,
					serviceTemplate: template,
					systemAuthenticationTypeId: this.selectedSystemForService.authenticationTypeId
				}
			})
		},
		getSystemTemplate (systemIntegration) {
			// Try to match the system with a template
			// This could be based on the system name or other properties
			if (!systemIntegration) return null

			// Find template by GUID match
			const template = SYSTEM_TEMPLATES.find(t =>
				t.guid === systemIntegration.templateGUID
			)

			if (template) return template
		},
		closeManageSystemModal () {
			this.showManageSystemModal = false
			this.systemToEdit = null
			this.cloneMode = false
			this.selectedTemplate = null
			this.showBackButton = false
		},
		onBackFromManageSystem () {
			this.showManageSystemModal = false
			if (this.selectedTemplate) {
				this.showTemplateSelectionModal = true
			} else {
				this.showSystemCreationOptionsModal = true
			}
		},
		onSystemCreated (systemIntegration) {
			this.closeManageSystemModal()
			this.loadSystemIntegrations()
		},
		async onImportSuccess () {
			showSnackbar('System imported successfully')
			this.showSystemCreationOptionsModal = false
			await this.loadSystemIntegrations()
			await this.loadIntegrationServices()
		},
		onSystemUpdated (systemIntegration) {
			this.closeManageSystemModal()
			this.loadSystemIntegrations()
		},
		onEditSystem (systemIntegration) {
			this.systemToEdit = systemIntegration
			this.cloneMode = false
			this.selectedTemplate = null
			this.showBackButton = false
			this.showManageSystemModal = true
		},
		onExportSystem (systemIntegration) {
			this.selectedSystemForExport = systemIntegration
			this.showSystemExportSheet = true
		},
		onExportService (integrationService) {
			// Transform integrationService to match the required JSON payload structure
			const exportPayload = {
				// Direct mappings - these fields exist and match
				name: integrationService.name,
				cassieObjectId: integrationService.cassieObjectId,
				isEnabled: false,
				updateStandDak: integrationService.updateStandDak,
				brandId: integrationService.brandId,
				requestThrottlingWaitTimeMS: integrationService.requestThrottlingWaitTimeMS,
				requestUrl: integrationService.requestUrl,
				requestBody: this.parseJsonSafely(integrationService.requestBody),
				authenticationUrl: integrationService.authenticationUrl,
				httpMethod: integrationService.httpMethod,
				notes: integrationService.notes,
				customHeaders: integrationService.customHeaders,
				successResponses: integrationService.successResponses,
				filters: integrationService.filters,

				// Handle null values - convert to empty strings
				objectIdColumn: integrationService.objectIdColumn,
				objectClientUrnColumn: integrationService.objectClientUrnColumn,
				trigger: integrationService.trigger,
				objectFilterClause: integrationService.objectFilterClause,
				objectSelectList: Array.isArray(integrationService.objectSelectList)
					? integrationService.objectSelectList.join(',')
					: integrationService.objectSelectList || '',
				clientTargetObjectName: integrationService.clientTargetObjectName,
				targetSystemAction: integrationService.targetSystemAction,
				returnedValue: integrationService.returnedValue
			}

			// Create and download JSON file
			const fileName = `${integrationService.name.replace(/[^a-zA-Z0-9]/g, '_')}_service_export.json`
			this.downloadJsonFile(exportPayload, fileName)

			// Show success message
			showSnackbar(`Integration "${integrationService.name}" downloaded successfully`)
		},
		onCloneSystem (systemIntegration) {
			this.systemToEdit = systemIntegration
			this.cloneMode = true
			this.selectedTemplate = null
			this.showBackButton = false
			this.showManageSystemModal = true
		},
		fallbackCopyToClipboard (text, systemName) {
			// Fallback method using textarea for older browsers
			const textarea = document.createElement('textarea')
			textarea.value = text
			textarea.style.position = 'fixed'
			textarea.style.opacity = '0'
			document.body.appendChild(textarea)
			textarea.select()

			try {
				document.execCommand('copy')
				showSnackbar(`System "${systemName}" copied to clipboard`)
			} catch (err) {
				console.error('Fallback copy failed:', err)
				showSnackbar(`Failed to copy system to clipboard`, 'error')
			} finally {
				document.body.removeChild(textarea)
			}
		},
		async loadIntegrationServiceFailures () {
			const { data: integrationFailures } = await getIntegrationFailures()
			this.integrationServiceFailures = integrationFailures.failures
		},
		getServicesForSystem (systemIntegration) {
			if (!systemIntegration || !this.integrationServices) return []
			return this.integrationServices.filter(service => service.integrationId === systemIntegration.id)
		},
		parseJsonSafely (jsonString) {
			try {
				if (!jsonString || typeof jsonString !== 'string') {
					return {}
				}
				return JSON.parse(jsonString)
			} catch (error) {
				console.warn('Failed to parse JSON, returning as string:', jsonString, error)
				return jsonString
			}
		},
		downloadJsonFile (data, fileName) {
			const dataStr = JSON.stringify(data, null, 2)
			const blob = new Blob([dataStr], { type: 'application/json' })
			const url = URL.createObjectURL(blob)

			const link = document.createElement('a')
			link.href = url
			link.download = fileName
			link.click()

			URL.revokeObjectURL(url)
		}
	}
}
</script>
